import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Drawer, IconButton } from "@material-ui/core";
import { Close } from "@material-ui/icons";
import StatusBadge from "./StatusBadge";
import ConversationTimeline from "./ConversationTimeline";
import CustomerServiceReply from "./CustomerServiceReply";
import PenaltyAppealForm from "./PenaltyAppealForm";
import { isCustomerServiceClosed, isPenaltyClosed } from "../mapCtripEvents";
import { formatDateTime } from "../complaintApi";
import { toastMessage } from "../../../../helpers/toastMessage";
import ThunksRateModule from "../../../../redux/actions/thunks/rateModule";
import {
  applyReplyToComplaint,
  buildCustomerServiceReplyBody,
  buildPenaltyReplyBody,
  getThunkErrorMessage,
  isThunkError,
  uploadCtripFiles,
} from "../ctripReplyHelpers";
import "./complaintManagement.css";

const ComplaintDetailDrawer = ({ open, complaint, onClose, onUpdate }) => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.userInfo.user);
  
  const [submitting, setSubmitting] = useState(false);

  if (!complaint) return null;

  const operator = user?.email || user?.fullName || "Vendor";
  const isPenalty = complaint.eventSource === 2;
  const isClosed = isPenalty
    ? isPenaltyClosed(complaint)
    : isCustomerServiceClosed(complaint);

  const sendReply = async (body, replyContent, eventSource, appealOperate) => {
    const response = await dispatch(
      ThunksRateModule.replyToEventCtripThunk(complaint.id, body),
    );

    if (isThunkError(response)) {
      throw new Error(
        getThunkErrorMessage(response, "Failed to submit response"),
      );
    }

    if (response?.msgCode !== "OK") {
      throw new Error(response?.message || "Failed to submit response");
    }

    return applyReplyToComplaint(
      complaint,
      replyContent,
      eventSource,
      appealOperate,
    );
  };

  const handleReply = async ({ message, attachments = [], eventTypeId }) => {
    try {
      setSubmitting(true);

      const enclosure = await uploadCtripFiles(
        dispatch,
        ThunksRateModule.uploadFileCtripThunk,
        attachments,
      );
      

      const body = buildCustomerServiceReplyBody(
        complaint,
        { content: message, enclosure, eventTypeId },
        operator,
      );

      const updated = await sendReply(
        body,
        message,
        complaint.eventSource ?? 1,
      );

      onUpdate(updated);
      toastMessage("success", "Reply submitted successfully");
      onClose();
    } catch (error) {
      toastMessage("error", error?.message || "Failed to submit reply");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAppeal = async ({
    action,
    comments,
    attachments = [],
    eventTypeId,
  }) => {
    try {
      setSubmitting(true);

      const enclosure = await uploadCtripFiles(
        dispatch,
        ThunksRateModule.uploadFileCtripThunk,
        attachments,
      );

      const body = buildPenaltyReplyBody(
        complaint,
        { action, comments, enclosure, eventTypeId },
        operator,
      );

      const replyContent =
        action === "accept" ? "Penalty accepted" : comments;

      const updated = await sendReply(
        body,
        replyContent,
        2,
        body.appealOperate,
      );

      onUpdate(updated);
      toastMessage(
        "success",
        action === "accept"
          ? "Penalty accepted"
          : "Appeal submitted successfully",
      );
      onClose();
    } catch (error) {
      toastMessage("error", error?.message || "Failed to submit appeal");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="cmp-drawer">
        <div className="cmp-drawer-header">
          <div>
            <h2 className="cmp-drawer-title">
              Event #{complaint.ctripEventId}
            </h2>
            <StatusBadge status={complaint.status} />
          </div>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </div>

        <div className="cmp-drawer-body">
          <Section title="Order Information">
            <div className="cmp-info-grid">
              <InfoRow label="Order ID" value={complaint.fullOrderId} />
              <InfoRow label="Vendor Order" value={complaint.vendorOrderId} />
              <InfoRow
                label="Service Provider"
                value={complaint.serviceProvider}
              />
            </div>
          </Section>

          <Section title="Event Information">
            <div className="cmp-info-grid">
              <InfoRow label="Type" value={complaint.eventType} />
              <InfoRow label="Category" value={complaint.category} />
              <InfoRow label="Subject" value={complaint.subject} />
              {isPenalty && complaint.penalty?.secondFoulReason && (
                <InfoRow
                  label="Sub Reason"
                  value={complaint.penalty.secondFoulReason}
                />
              )}
              {!isPenalty && complaint.eventDetail?.eventTypeId && (
                <InfoRow
                  label="Event Type ID"
                  value={complaint.eventDetail.eventTypeId}
                />
              )}
              <InfoRow
                label="Deadline"
                value={formatDateTime(complaint.deadline)}
              />
            </div>
          </Section>

          <Section title={isPenalty ? "Penalty Description" : "Complaint"}>
            <p className="cmp-complaint-text">
              <strong>{isPenalty ? "Ctrip says:" : "Customer says:"}</strong>
              <br />
              {complaint.complaintText}
            </p>
          </Section>

          {complaint.attachments?.length > 0 && (
            <Section title="Attachments">
              <div className="cmp-attachments">
                {complaint.attachments.map((file, i) => (
                  <a
                    key={i}
                    href={file.url}
                    className="cmp-attachment-chip"
                    onClick={(e) => e.preventDefault()}
                  >
                    📎 {file.name}
                  </a>
                ))}
              </div>
            </Section>
          )}

          <Section title="Conversation Timeline">
            <ConversationTimeline timeline={complaint.timeline} />
          </Section>

          <div className="cmp-divider" />

          {isPenalty ? (
            <PenaltyAppealForm
              complaint={complaint}
              onSubmit={handleAppeal}
              loading={submitting}
            />
          ) : (
            !isClosed && (
              <CustomerServiceReply
                complaint={complaint}
                onSubmit={handleReply}
                loading={submitting}
              />
            )
          )}
        </div>
      </div>
    </Drawer>
  );
};

const Section = ({ title, children }) => (
  <div className="cmp-section">
    <h4 className="cmp-section-title">{title}</h4>
    {children}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div className="cmp-info-row">
    <span className="cmp-info-label">{label}</span>
    <span className="cmp-info-value">{value || "-"}</span>
  </div>
);

export default ComplaintDetailDrawer;
